/**
 * Portfolio Actions Menu
 *
 * Displays action options when user taps the Add button.
 * Shows import from photo option only (other options commented out for future use).
 */

import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, Modal, Platform, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

// Conditionally import ActionSheetIOS only on native platforms
let ActionSheetIOS: any = undefined;
if (Platform.OS === 'ios' && !(Platform as any).isWeb) {
  const ReactNative = require('react-native');
  ActionSheetIOS = ReactNative.ActionSheetIOS;
}

interface PortfolioActionsProps {
  visible: boolean;
  onClose: () => void;
  onAddPosition: () => void;
  onAddCash: () => void;
  onAddTrade: () => void;
  onImportPhoto: () => void;
  onAddCorporateAction?: () => void;
  onSimulate?: () => void;
}

export function PortfolioActions({
  visible,
  onClose,
  onAddPosition,
  onAddCash,
  onAddTrade,
  onImportPhoto,
  onAddCorporateAction,
  onSimulate,
}: PortfolioActionsProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const run = (callback?: () => void) => () => {
    if (callback) {
      callback();
      onClose();
    }
  };

  // Shared action list drives both the iOS ActionSheet and the modal menu so
  // the two platforms stay in sync and the cancel index is always correct.
  const actions: { label: string; onPress: () => void }[] = [
    {
      label: t('portfolio.addPosition', { defaultValue: 'Add Position' }),
      onPress: run(onAddPosition),
    },
    { label: t('portfolio.addCash', { defaultValue: 'Add Cash' }), onPress: run(onAddCash) },
    { label: t('portfolio.addTrade', { defaultValue: 'Add Trade' }), onPress: run(onAddTrade) },
  ];
  if (onSimulate) {
    actions.push({
      label: t('portfolio.simulateImpact', { defaultValue: 'Simulate impact' }),
      onPress: run(onSimulate),
    });
  }
  if (onAddCorporateAction) {
    actions.push({
      label: t('portfolio.addCorporateAction', { defaultValue: 'Add Corporate Action' }),
      onPress: run(onAddCorporateAction),
    });
  }
  actions.push({
    label: t('portfolio.importFromPhoto', { defaultValue: 'Import from Photo' }),
    onPress: run(onImportPhoto),
  });

  // iOS: Use native ActionSheet
  const showActionSheet = () => {
    if (!ActionSheetIOS) return; // Not available on web/Android

    const options = [
      ...actions.map((a) => a.label),
      t('common.cancel', { defaultValue: 'Cancel' }),
    ];

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex: options.length - 1,
      },
      (buttonIndex: number) => {
        if (buttonIndex >= 0 && buttonIndex < actions.length) {
          actions[buttonIndex].onPress();
        }
      }
    );
  };

  // Android/Web: Show custom modal menu
  if (Platform.OS === 'ios' && !(Platform as any).isWeb && ActionSheetIOS) {
    if (visible) {
      showActionSheet();
    }
    return null;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <View style={styles.menuContainer}>
          <View style={styles.menu}>
            {actions.map((action, index) => (
              <TouchableOpacity
                key={action.label}
                style={[styles.menuItem, index === actions.length - 1 && { borderBottomWidth: 0 }]}
                onPress={action.onPress}
              >
                <Text style={styles.menuItemText}>{action.label}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={[styles.menuItem, styles.cancelButton]} onPress={onClose}>
              <Text style={[styles.menuItemText, styles.cancelButtonText]}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    menuContainer: {
      padding: 16,
    },
    menu: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      overflow: 'hidden',
      ...Platform.select({
        web: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      }),
    },
    menuItem: {
      paddingVertical: 16,
      paddingHorizontal: 20,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    menuItemText: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
      textAlign: 'center',
    },
    cancelButton: {
      borderBottomWidth: 0,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    cancelButtonText: {
      color: theme.colors.textSecondary,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
